import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Plus, CheckCircle2, Zap, MessageCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { resellerApi, validatePlayer } from "../lib/api";
import { fmtARS, STATUS_LABEL, STATUS_DOT } from "../lib/utils-app";
import PackageCard from "../components/PackageCard";
import ChytaPayDialog from "../components/ChytaPayDialog";

function BalanceCard({ data, onPaid }) {
  const [amount, setAmount] = useState(String(data.min_topup));
  const [busy, setBusy] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const n = parseInt(amount || "0", 10) || 0;

  async function topup() {
    if (n < data.min_topup) return toast.error(`La carga mínima es ${fmtARS(data.min_topup)}`);
    setBusy(true);
    try {
      setPayOrder(await resellerApi.topup(n));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando el cobro");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-amber-400/40 bg-[#0d0d0d] p-5" data-testid="reseller-balance-card">
      <ChytaPayDialog order={payOrder} onClose={() => { setPayOrder(null); onPaid(); }} />
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400">
        <Wallet className="w-4 h-4 text-amber-300" /> Tu saldo
      </div>
      <div className="font-display text-5xl tracking-wider text-amber-300 mt-1" data-testid="reseller-balance">{fmtARS(data.balance_ars)}</div>
      <div className="mt-4 text-xs uppercase tracking-widest text-neutral-500">Cargar saldo por transferencia (mínimo {fmtARS(data.min_topup)})</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {[30000, 50000, 100000].map((v) => (
          <button key={v} onClick={() => setAmount(String(v))} data-testid={`reseller-quick-${v}`}
            className={`px-3 py-1.5 rounded-md text-xs border ${n === v ? "border-amber-300 text-amber-300" : "border-white/10 text-neutral-300 hover:bg-white/5"}`}>
            {fmtARS(v)}
          </button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
        <input type="number" inputMode="numeric" min={data.min_topup} value={amount} onChange={(e) => setAmount(e.target.value.slice(0, 8))}
          data-testid="reseller-topup-input" className="min-w-0 px-4 py-3 rounded-md bg-black border border-amber-400/40 text-white" />
        <button onClick={topup} disabled={busy || n < data.min_topup} data-testid="reseller-topup-btn"
          className="px-5 py-3 rounded-md bg-amber-400 hover:bg-amber-300 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {busy ? "Generando..." : "Cargar saldo"}
        </button>
      </div>
    </div>
  );
}

function RechargeForm({ data, onDone }) {
  const [playerId, setPlayerId] = useState("");
  const [valid, setValid] = useState(null);
  const [checking, setChecking] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);

  async function validate() {
    setChecking(true);
    try {
      const r = await validatePlayer(playerId, "diamonds");
      setValid(r);
      if (!r.valid) toast.error(r.error || "ID inválido");
    } catch {
      toast.error("Error validando");
    } finally {
      setChecking(false);
    }
  }

  async function recharge() {
    setBusy(true);
    try {
      const r = await resellerApi.recharge({ package_id: pkg.id, player_id: playerId, player_nickname: valid?.nickname || null });
      toast.success(`Recarga enviada · saldo ${fmtARS(r.balance_ars)}`);
      setPkg(null);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la recarga");
    } finally {
      setBusy(false);
    }
  }

  const can = valid?.valid && pkg && !busy && data.balance_ars >= pkg.price_ars;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 max-w-xl">
        <input value={playerId} onChange={(e) => { setPlayerId(e.target.value.replace(/\D/g, "").slice(0, 15)); setValid(null); }}
          placeholder="ID de Free Fire del cliente" data-testid="reseller-player-id"
          className="min-w-0 px-4 py-3 rounded-md bg-black border border-[#ff4500]/50 text-white tracking-wider" />
        <button onClick={validate} disabled={playerId.length < 6 || checking} data-testid="reseller-validate-btn"
          className="btn-primary px-5 py-3 rounded-md text-sm disabled:opacity-50">{checking ? "Validando..." : "Validar"}</button>
      </div>
      {valid?.valid && (
        <div className="text-sm text-emerald-400 flex items-center gap-1.5" data-testid="reseller-valid">
          <CheckCircle2 className="w-4 h-4" /> ID válido{valid.nickname ? ` · ${valid.nickname}` : ""}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {data.packages.map((p) => (
          <PackageCard key={p.id} pkg={p} selected={pkg?.id === p.id} onSelect={setPkg} />
        ))}
      </div>
      <button onClick={recharge} disabled={!can} data-testid="reseller-recharge-btn"
        className="w-full sm:w-auto px-8 py-4 rounded-md btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
        <Zap className="w-5 h-5" />
        {pkg ? `Recargar ${pkg.diamonds} con saldo (${fmtARS(pkg.price_ars)})` : "Elegí un paquete"}
      </button>
      {pkg && data.balance_ars < pkg.price_ars && <div className="text-sm text-rose-400" data-testid="reseller-no-balance">Saldo insuficiente para este paquete.</div>}
    </div>
  );
}

function History({ orders, adjustments = [] }) {
  const rows = [
    ...orders.map((o) => ({ key: o.order_id, at: o.created_at, dot: STATUS_DOT[o.status] || "bg-neutral-500",
      title: o.category === "reseller_topup" ? "Carga de saldo" : `${o.product_name} · ID ${o.player_id}`,
      sub: STATUS_LABEL[o.status] || o.status, amount: o.category === "reseller_topup" ? o.amount_ars : -o.amount_ars })),
    ...adjustments.map((a) => ({ key: a.entry_id, at: a.created_at, dot: "bg-violet-400",
      title: "Ajuste de saldo", sub: "Realizado por el administrador", amount: a.delta })),
  ].sort((x, y) => (x.at < y.at ? 1 : -1));
  if (!rows.length) return <div className="text-sm text-neutral-500" data-testid="reseller-history-empty">Todavía no hay movimientos.</div>;
  return (
    <div className="divide-y divide-white/5 rounded-xl border border-white/10" data-testid="reseller-history">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-3 px-4 py-3 text-sm">
          <span className={`w-2 h-2 rounded-full shrink-0 ${r.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="text-white truncate">{r.title}</div>
            <div className="text-[11px] text-neutral-500">{new Date(r.at).toLocaleString("es-AR")} · {r.sub}</div>
          </div>
          <div className={r.amount >= 0 ? "text-emerald-400" : "text-white"}>
            {r.amount >= 0 ? "+" : "-"}{fmtARS(Math.abs(r.amount))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Revendedores() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    resellerApi.me().then(setData).catch(() => setDenied(true));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading) return <div className="min-h-[60vh]" />;
  if (!user || denied) {
    return (
      <div className="min-h-[60vh] max-w-xl mx-auto px-4 py-20 text-center" data-testid="reseller-denied">
        <Wallet className="w-12 h-12 text-amber-300 mx-auto mb-4" />
        <h1 className="font-display text-4xl tracking-wider text-white">Zona revendedores</h1>
        <p className="text-neutral-400 mt-2">Esta sección es solo para revendedores autorizados. Si querés revender, escribinos.</p>
        <a href="https://wa.me/543821452181?text=Hola!%20Quiero%20ser%20revendedor" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md bg-emerald-500 text-black font-bold" data-testid="reseller-wa-link">
          <MessageCircle className="w-4 h-4" /> Quiero ser revendedor
        </a>
        <div className="mt-4"><Link to="/" className="text-sm text-[#ff4500] underline">Volver al inicio</Link></div>
      </div>
    );
  }
  if (!data) return <div className="min-h-[60vh]" />;

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-10" data-testid="reseller-page">
      <div>
        <div className="text-xs tracking-[0.3em] text-amber-300">PANEL REVENDEDOR</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-wider text-white">Recargas con saldo</h1>
        <p className="text-neutral-400 mt-2 max-w-2xl">Cargá saldo por transferencia y usalo para recargar diamantes a tus clientes al instante, con precios de revendedor.</p>
      </div>
      <BalanceCard data={data} onPaid={load} />
      <section>
        <h2 className="font-display text-3xl tracking-wider text-white mb-4">Nueva recarga</h2>
        <RechargeForm data={data} onDone={load} />
      </section>
      <section>
        <h2 className="font-display text-3xl tracking-wider text-white mb-4">Movimientos</h2>
        <History orders={data.orders} adjustments={data.adjustments} />
      </section>
    </div>
  );
}
